import {
  mergeContentWithoutBump,
  mergeSyncState,
  mergeAttachmentWithoutBump,
} from "@js/helpers/state.js";

/********************************************************************

BACKGROUND SAVE
---------------

The background save system's job is to take the data that is in the local Redux
store and put it into the GreenArrow database. It accomplishes this in the
following way:

1. When the Redux store is updated, "state.sync.data_generation_id" is incremented.

2. When the store is updated (via the "store.subscribe" hook), we check to see if
   the data generation id is different than "state.sync.last_published_generation_id".

3. If the data generation indicates it's time to save and one is not already in
   progress (state.sync.publish_in_progress), a background save begins.

Updating contents uses an internal API [Routes.api_v2_campaign_update_path(campaignID)].

JSON that is sent to the server:

* campaign
  * contents -- array of objects
    * id -- integer
      * Used to match this content to existing contents (to allow updating
      * instead of delete/insert). If you pass up an id that does not
      * exist on this campaign, it's not an error. We just create a new
      * content, and then return to you the id of the created content.
      * New contents will be generated client-side using id=null.
    * client_content_id -- string
      * The server does not save this value, it's just passed through into the
      * response so the client can collate new contents.
    * name -- string
    * format -- string: "html", "text", "both"
    * subject -- string
    * active_html_editor -- string: "bee", "wysiwyg", "textarea"
    * bee_json -- string of JSON
    * html -- string
    * text -- string
    * verification_sha256 -- object
      * bee_json -- sha256 hash of what "bee_json" must be
      * html -- sha256 hash of what "html" must be
      * text -- sha256 hash of what "text" must be
  * attachments -- array of objects
    * id -- integer
    * client_attachment_id -- string
    * attachment_name -- string
    * sha256 -- string
    * data_base64 -- string

JSON response:

* result -- string
  * this will be saved, validation_failed, or verification_sha256_mismatch
* campaign_id -- integer
* validation_errors -- array of error strings that apply globally
* contents -- array of objects
  * id -- the true content ID that exists in the database (may not match what you passed up)
  * client_content_id -- we return to you whatever you passed up
  * validation_errors -- object (where key is "html"/"subject"/etc; value is a list of errors) -- validation errors
  * webview_link -- link to the webview for this content, if html
  * verification_sha256_mismatch -- object
    * html -- sha256 that the server has
    * text -- sha256 that the server has
    * bee_json -- sha256 that the server has
  * published_sha256 -- object
    * html -- sha256 that the server has
    * text -- sha256 that the server has
    * bee_json -- sha256 that the server has
* attachments -- array of objects
  * id -- string
  * client_attachment_id -- string
  * validation_errors -- object
  * sha256 -- string


Partial Updates
---------------

When the JavaScript is building the data that is sent to the server to update
the campaign, it reviews each content's "published_sha256" values. These are
the sha256 checksums of the html/text/bee_json values that we "think" are in
the server's database.

If the data hasn't changed from those published_sha256 values, then we will
only include "verification_sha256" for that field and NOT the whole value.

The server will then verify this. If there's a mismatch, it will respond with
verification_sha256_mismatch and not update the database.

The JavaScript will then update its "published_sha256" values and retry the
write. This time, the actual value is included.

This saves us from sending the full text of every content every time we save.


Error Handling
--------------

Errors are handled in the following order:

1. If there is any kind of communications error or non-OK response from the
   HTTP session, we consider this an error and do not further process any response
   we have.

2. If the server responds with a "sha256 mismatch" indicating that the data we
   thought was in the database is not in fact there, we update our state for the
   actual sha256 in the database and will retry the background save.

3. If the server responds with a validation error and we are not caught-up, we
   do not store that error. Instead we just end the save and await the next
   background save.

4. If the server responds with validation errors and we are caught up, those errors
   are stored in the state and we will present them to the user.


Redux State
-----------

* sync
  * publish_in_progress -- integer
    * We are currently in the process of publishing to the server. Only one background
    * save may run concurrently.
  * data_generation_id -- integer
    * The current data generation id. This is updated each time we make a change
    * to the state that should result in a background save.
  * last_published_generation_id -- integer
    * The most recent version that was published to the server. This is updated
    * if we get a validation error, but not a communications error.
  * last_publish_had_validation_errors -- boolean
    * The most recent background save ended with the background save being caught up and it had
    * a validation error.
  * network_failure_count -- integer
    * set to 0 whenever we get a non-network-failure response from server (success or validation error)
    * set to 0 whenever "saving an update to the redux store"
    * incremented whenever there is a network failure as a result of a sync
  * network_failure_stopped -- boolean
    * This field is set to true when we want to stop trying due to network errors.
  * show_error_banner -- boolean
    * The error banner is currently being shown.


Client-side initialization
--------------------------

set state.sync to:

  state.sync = {
    data_generation_id: 1,
    last_published_generation_id: 1,
    last_publish_had_validation_errors: false,
    network_failure_count: 0,
    network_failure_stopped: false,
    publish_in_progress: false,
  }

set state.data to:

* All data presented by Ruby.
* Ruby generates "published_sha256" values that match the existing content.
* client_content_id will be ini'ed from a UUID client-side.


Client-side "save an update to the redux store"
-----------------------------------------------

* Contents are directly manipulated by the mergeContent (or
  mergeContentWithoutBump) helper functions.

* mergeContent increments "state.sync.data_generation_id", causing a background save to begin
* mergeContentWithoutBump does not increment that value


Client-side saving to server
----------------------------

* When starting save:
   * Set redux.sync.publish_in_progress = true
   * store redux.sync.data_generation_id in a local variable named "publishing_this_data_generation_id"
   * Kick off an API request
      * when the published_sha256 matches the sha256 of the value of html/text/bee_json in a content object, then in what we send to the server
         * set the verification_sha256 of the html/text/bee_json
         * do not send the html/text/bee_json key in the content object

* As a result of the save:
   * If there are network errors:
      * Set last_publish_had_validation_errors = false
      * Increment redux.sync.network_failure_count
      * Set network_failure_stopped = redux.sync.network_failure_count >= 3 ? true : false
      * Set publish_in_progress = false
   * If there are "verification_sha256_mismatch" errors:
      * Update the corresponding "published_sha256" (and match by client_content_id)
      * Set last_publish_had_validation_errors = false
      * Set network_failure_count = 0
      * Set network_failure_stopped = false
      * Set publish_in_progress = false
   * If there are validation errors:
      * If "publishing_this_data_generation_id" == redux.sync.data_generation_id
         * then
            * save what the errors are to "validation_errors" in the redux.data.contents
            * set last_published_generation_id = "publishing_this_data_generation_id"
            * Set last_publish_had_validation_errors = true
            * Set network_failure_count = 0
            * Set network_failure_stopped = false
            * Set publish_in_progress = false
         * else
            * Set last_publish_had_validation_errors = false
            * Set network_failure_count = 0
            * Set network_failure_stopped = false
            * Set publish_in_progress = false
   * If no errors of any kind (successful save to database):
      * For each content (matched by client_content_id; it is OK the server returns a client_content_id that we can not find in the redux store; just skip updating anything in redux for that content from the server; this means the content in question was deleted by the user after we started the save, and we should be triggering a new save right after this):
         * set these keys to what was returned by the server:
            * content_id
            * published_sha256
      * Set redux.sync.last_published_generation_id = the local variable "publishing_this_data_generation_id"
      * Set last_publish_had_validation_errors = false
      * Set network_failure_count = 0
      * Set network_failure_stopped = false
      * Set publish_in_progress = false


When the redux store is updated, the decision will be made to trigger a
background save. See the `helpers/store.js` file and "subscribe" callback on
the store.


Also in that unsubscribe, we prompt the user if we are still working on a
background save in the `window.onbeforeunload` handler.


*********************************************************************/

import {
  CE_BACKGROUND_SAVE_STARTED,
  CE_BACKGROUND_SAVE_SUCCESS,
  CE_BACKGROUND_SAVE_FAILURE,
  CE_BACKGROUND_SAVE_ERROR_BANNER_HIDE,
  CE_BACKGROUND_SAVE_ERROR_BANNER_SHOW,
} from "@js/actions/studio/background_save.js";

import { updateContentPublishedSha256 } from "@js/helpers/state.js";

export default (state = {}, action) => {
  switch (action.type) {
    case CE_BACKGROUND_SAVE_STARTED: {
      return mergeSyncState(state, {
        publish_in_progress: true,
        show_error_banner: false,
      });
    }

    case CE_BACKGROUND_SAVE_SUCCESS: {
      let updatedState = mergeSyncState(state, {
        publish_in_progress: false,
        network_failure_count: 0,
        network_failure_stopped: false,
        show_error_banner: false,
        validation_errors: false,
      });

      let contents = action.response && action.response.contents;
      let attachments = action.response && action.response.attachments;
      let result = action.response && action.response.result;
      let validation_errors =
        action.response && action.response.validation_errors;

      // Did the server reject it for verification sha256 mismatch? If so, then
      // we just record the new remote checksum. The background save system will
      // take over from there.

      if (result === "verification_sha256_mismatch") {
        for (let content of contents) {
          for (let field in content.verification_sha256_mismatch) {
            if (content.verification_sha256_mismatch[field]) {
              updatedState = updateContentPublishedSha256(
                updatedState,
                content.client_content_id,
                field,
                content.verification_sha256_mismatch[field],
              );
            }
          }
        }

        return updatedState;
      }

      // Check if we're going to show the users errors, or just wait and try again.

      if (result === "validation_failed") {
        if (action.data_generation_id === state.sync.data_generation_id) {
          // If the version we wrote is still the newest version and we got errors,
          // save those errors and update last_published_generation_id.

          for (let content of contents) {
            if (content.validation_errors) {
              updatedState = mergeContentWithoutBump(
                updatedState,
                content.client_content_id,
                {
                  validation_errors: content.validation_errors,
                },
              );
            }
          }

          for (let attachment of attachments) {
            if (attachment.validation_errors) {
              updatedState = mergeAttachmentWithoutBump(
                updatedState,
                attachment.client_attachment_id,
                {
                  validation_errors: attachment.validation_errors,
                },
              );
            }
          }

          if (validation_errors) {
            updatedState = mergeSyncState(updatedState, {
              validation_errors: validation_errors,
            });
          }

          return mergeSyncState(updatedState, {
            last_publish_had_validation_errors: true,
            last_published_generation_id: action.data_generation_id,
          });
        } else {
          // We weren't caught up, so we'll leave last_published_generation_id
          // where it was & consider that we've had no validation errors. Pretend
          // this error just didn't happen, since a new sync is about to start.

          return mergeSyncState(updatedState, {
            last_publish_had_validation_errors: false,
          });
        }
      }

      // If we didn't have any errors...

      if (result === "saved") {
        // Update the local id and published sha256s based on the values returned
        // from the server.

        for (let content of contents) {
          updatedState = mergeContentWithoutBump(
            updatedState,
            content.client_content_id,
            {
              id: content.id,
              published_sha256: content.published_sha256,
            },
          );
          if (!isNil(content.html)) {
            // The server modified the html and has the updated version for us
            updatedState = mergeContentWithoutBump(
              updatedState,
              content.client_content_id,
              {
                html: content.html,
              },
            );
          }
          if (!isNil(content.text)) {
            // The server modified the text and has the updated version for us
            updatedState = mergeContentWithoutBump(
              updatedState,
              content.client_content_id,
              {
                text: content.text,
              },
            );
          }
          if (!isNil(content.webview_link)) {
            // The server generated a webview link for us
            updatedState = mergeContentWithoutBump(
              updatedState,
              content.client_content_id,
              {
                webview_link: content.webview_link,
              },
            );
          }

          updatedState = mergeContentWithoutBump(
            updatedState,
            content.client_content_id,
            {
              unsub_tag_missing: content.unsub_tag_missing,
            },
          );
        }

        // Update the local id based on the values returned from the server. We
        // no longer need attachment data, since the save was successful - so
        // we'll clear it out.

        for (let attachment of attachments) {
          updatedState = mergeAttachmentWithoutBump(
            updatedState,
            attachment.client_attachment_id,
            {
              id: attachment.id,
              data_base64: null,
            },
          );
        }

        // We had no errors and we update last_published_generation_id.

        return mergeSyncState(updatedState, {
          last_publish_had_validation_errors: false,
          last_published_generation_id: action.data_generation_id,
        });
      }

      // The server returned something that we don't really recognize.

      return updatedState;
    }

    case CE_BACKGROUND_SAVE_FAILURE: {
      // We've received some kind of communications error in the background sync.

      let failureCount = state.sync.network_failure_count + 1;

      return mergeSyncState(state, {
        publish_in_progress: false,
        last_publish_had_validation_errors: false,
        network_failure_count: failureCount,
        network_failure_stopped: failureCount >= 3,
        show_error_banner: false,
      });
    }

    case CE_BACKGROUND_SAVE_ERROR_BANNER_SHOW:
      // The user has requested we show the error banner.

      return mergeSyncState(state, {
        show_error_banner: true,
      });

    case CE_BACKGROUND_SAVE_ERROR_BANNER_HIDE:
      // The user has requested we hide the error banner.

      return mergeSyncState(state, {
        show_error_banner: false,
      });

    default:
      return state;
  }
};
